// src/components/blog/BlogSidebar.jsx
import React, { useState, useEffect } from 'react';
import { designTokens } from '../../theme/designTokens';
import {
    Facebook,
    Twitter,
    Linkedin,
    Mail,
    Share2,
    Bookmark,
    TrendingUp,
    Hash
} from 'lucide-react';
import { Link } from 'react-router-dom';
import {BlogService} from "../../api/blog.api";

const BlogSidebar = ({ relatedPosts = [], currentCategory, shareUrls = {} }) => {
    const [categories, setCategories] = useState([]);
    const [popularPosts, setPopularPosts] = useState([]);
    const [tags, setTags] = useState([]);
    const [isBookmarked, setIsBookmarked] = useState(false);

    useEffect(() => {
        // Fetch categories
        BlogService.getBlogCategories().then(res => {
            if (res.success) setCategories(res.data);
        });

        // Fetch popular posts
        BlogService.getPopularBlogs(5).then(res => {
            if (res.success) setPopularPosts(res.data);
        });

        // Fetch tags
        BlogService.getTagsCloud().then(res => {
            if (res.success) setTags(res.data);
        });
    }, []);

    const toggleBookmark = () => {
        setIsBookmarked(!isBookmarked);
        // In real app, save to localStorage or API
    };

    return (
        <div style={{
            position: 'sticky',
            top: '100px',
            height: 'fit-content',
        }}>
            {/* Share Widget */}
            <div style={{
                background: 'white',
                borderRadius: '1rem',
                border: `1px solid ${designTokens.colors.border.light}`,
                padding: '1.5rem',
                marginBottom: '1.5rem',
            }}>
                <h4 style={{
                    fontFamily: designTokens.typography.fontFamilies.display,
                    fontSize: '1rem',
                    fontWeight: 500,
                    color: designTokens.colors.text.primary,
                    marginBottom: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                }}>
                    <Share2 size={16} />
                    Share This Article
                </h4>
                <div style={{
                    display: 'flex',
                    gap: '0.5rem',
                    flexWrap: 'wrap',
                }}>
                    {Object.entries(shareUrls).map(([platform, url]) => {
                        const icons = {
                            facebook: Facebook,
                            twitter: Twitter,
                            linkedin: Linkedin,
                            email: Mail,
                        };
                        const Icon = icons[platform];
                        if (!Icon) return null;

                        return (
                            <a
                                key={platform}
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                    width: '36px',
                                    height: '36px',
                                    borderRadius: '50%',
                                    background: `${designTokens.colors.champagne}10`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: designTokens.colors.champagne,
                                    textDecoration: 'none',
                                    transition: 'all 0.2s ease',
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.background = designTokens.colors.champagne;
                                    e.target.style.color = 'white';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.background = `${designTokens.colors.champagne}10`;
                                    e.target.style.color = designTokens.colors.champagne;
                                }}
                                title={`Share on ${platform}`}
                            >
                                <Icon size={16} />
                            </a>
                        );
                    })}
                </div>
                <button
                    onClick={toggleBookmark}
                    style={{
                        width: '100%',
                        marginTop: '1rem',
                        padding: '0.75rem',
                        borderRadius: '0.5rem',
                        background: isBookmarked ? designTokens.colors.champagne : 'transparent',
                        border: `1px solid ${isBookmarked ? designTokens.colors.champagne : designTokens.colors.border.light}`,
                        color: isBookmarked ? 'white' : designTokens.colors.text.secondary,
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        transition: 'all 0.2s ease',
                    }}
                >
                    <Bookmark size={14} fill={isBookmarked ? 'white' : 'transparent'} />
                    {isBookmarked ? 'Bookmarked' : 'Bookmark Article'}
                </button>
            </div>

            {/* Categories */}
            <div style={{
                background: 'white',
                borderRadius: '1rem',
                border: `1px solid ${designTokens.colors.border.light}`,
                padding: '1.5rem',
                marginBottom: '1.5rem',
            }}>
                <h4 style={{
                    fontFamily: designTokens.typography.fontFamilies.display,
                    fontSize: '1rem',
                    fontWeight: 500,
                    color: designTokens.colors.text.primary,
                    marginBottom: '1rem',
                }}>
                    Categories
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {categories.map((cat) => (
                        <Link
                            key={cat._id}
                            to={`/blog?category=${cat._id}`}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '0.5rem',
                                borderRadius: '0.5rem',
                                textDecoration: 'none',
                                color: cat._id === currentCategory
                                    ? designTokens.colors.champagne
                                    : designTokens.colors.text.secondary,
                                background: cat._id === currentCategory
                                    ? `${designTokens.colors.champagne}10`
                                    : 'transparent',
                                fontSize: '0.875rem',
                                transition: 'all 0.2s ease',
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.background = `${designTokens.colors.champagne}10`;
                                e.target.style.color = designTokens.colors.champagne;
                            }}
                            onMouseLeave={(e) => {
                                if (cat._id !== currentCategory) {
                                    e.target.style.background = 'transparent';
                                    e.target.style.color = designTokens.colors.text.secondary;
                                }
                            }}
                        >
                            <span style={{ textTransform: 'capitalize' }}>{cat._id}</span>
                            <span style={{
                                fontSize: '0.75rem',
                                color: designTokens.colors.text.tertiary,
                                background: `${designTokens.colors.border.light}30`,
                                padding: '0.125rem 0.5rem',
                                borderRadius: '9999px',
                            }}>
                {cat.count}
              </span>
                        </Link>
                    ))}
                </div>
            </div>

            {/* Popular Posts */}
            {popularPosts.length > 0 && (
                <div style={{
                    background: 'white',
                    borderRadius: '1rem',
                    border: `1px solid ${designTokens.colors.border.light}`,
                    padding: '1.5rem',
                    marginBottom: '1.5rem',
                }}>
                    <h4 style={{
                        fontFamily: designTokens.typography.fontFamilies.display,
                        fontSize: '1rem',
                        fontWeight: 500,
                        color: designTokens.colors.text.primary,
                        marginBottom: '1rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                    }}>
                        <TrendingUp size={16} />
                        Popular Articles
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {popularPosts.map((post) => (
                            <Link
                                key={post._id}
                                to={`/blog/${post.slug}`}
                                style={{
                                    display: 'block',
                                    textDecoration: 'none',
                                }}
                            >
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '0.75rem',
                                    padding: '0.5rem',
                                    borderRadius: '0.5rem',
                                    transition: 'all 0.2s ease',
                                }}
                                     onMouseEnter={(e) => {
                                         e.target.style.background = `${designTokens.colors.champagne}05`;
                                     }}
                                     onMouseLeave={(e) => {
                                         e.target.style.background = 'transparent';
                                     }}
                                >
                                    {post.coverImage?.url && (
                                        <img
                                            src={post.coverImage.url}
                                            alt={post.title}
                                            style={{
                                                width: '60px',
                                                height: '60px',
                                                borderRadius: '0.5rem',
                                                objectFit: 'cover',
                                            }}
                                        />
                                    )}
                                    <div style={{ flex: 1 }}>
                                        <div style={{
                                            fontSize: '0.875rem',
                                            fontWeight: 500,
                                            color: designTokens.colors.text.primary,
                                            marginBottom: '0.25rem',
                                            lineHeight: 1.4,
                                        }}>
                                            {post.title}
                                        </div>
                                        <div style={{
                                            fontSize: '0.75rem',
                                            color: designTokens.colors.text.tertiary,
                                        }}>
                                            {post.views?.toLocaleString()} views
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            )}

            {/* Related Posts */}
            {relatedPosts.length > 0 && (
                <div style={{
                    background: 'white',
                    borderRadius: '1rem',
                    border: `1px solid ${designTokens.colors.border.light}`,
                    padding: '1.5rem',
                    marginBottom: '1.5rem',
                }}>
                    <h4 style={{
                        fontFamily: designTokens.typography.fontFamilies.display,
                        fontSize: '1rem',
                        fontWeight: 500,
                        color: designTokens.colors.text.primary,
                        marginBottom: '1rem',
                    }}>
                        Related Articles
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {relatedPosts.map((post) => (
                            <Link
                                key={post._id}
                                to={`/blog/${post.slug}`}
                                style={{
                                    display: 'block',
                                    textDecoration: 'none',
                                }}
                            >
                                <div style={{
                                    padding: '0.75rem',
                                    borderRadius: '0.5rem',
                                    background: 'transparent',
                                    border: `1px solid ${designTokens.colors.border.light}`,
                                    transition: 'all 0.2s ease',
                                }}
                                     onMouseEnter={(e) => {
                                         e.target.style.background = `${designTokens.colors.champagne}05`;
                                         e.target.style.borderColor = designTokens.colors.champagne;
                                     }}
                                     onMouseLeave={(e) => {
                                         e.target.style.background = 'transparent';
                                         e.target.style.borderColor = designTokens.colors.border.light;
                                     }}
                                >
                                    <div style={{
                                        fontSize: '0.875rem',
                                        fontWeight: 500,
                                        color: designTokens.colors.text.primary,
                                        marginBottom: '0.25rem',
                                        lineHeight: 1.4,
                                    }}>
                                        {post.title}
                                    </div>
                                    <div style={{
                                        fontSize: '0.75rem',
                                        color: designTokens.colors.text.tertiary,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                    }}>
                    <span>
                      {new Date(post.date).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric'
                      })}
                    </span>
                                        <span>•</span>
                                        <span>{post.readingTime || 5} min read</span>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            )}

            {/* Tags Cloud */}
            {tags.length > 0 && (
                <div style={{
                    background: 'white',
                    borderRadius: '1rem',
                    border: `1px solid ${designTokens.colors.border.light}`,
                    padding: '1.5rem',
                }}>
                    <h4 style={{
                        fontFamily: designTokens.typography.fontFamilies.display,
                        fontSize: '1rem',
                        fontWeight: 500,
                        color: designTokens.colors.text.primary,
                        marginBottom: '1rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                    }}>
                        <Hash size={16} />
                        Popular Tags
                    </h4>
                    <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '0.5rem',
                    }}>
                        {tags.map((tag) => (
                            <Link
                                key={tag._id}
                                to={`/blog/tag/${tag._id}`}
                                style={{
                                    padding: '0.25rem 0.75rem',
                                    borderRadius: '9999px',
                                    background: `${designTokens.colors.champagne}10`,
                                    border: `1px solid ${designTokens.colors.champagne}20`,
                                    color: designTokens.colors.champagne,
                                    fontSize: '0.75rem',
                                    textDecoration: 'none',
                                    transition: 'all 0.2s ease',
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.background = designTokens.colors.champagne;
                                    e.target.style.color = 'white';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.background = `${designTokens.colors.champagne}10`;
                                    e.target.style.color = designTokens.colors.champagne;
                                }}
                            >
                                {tag._id}
                            </Link>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default BlogSidebar;